import React from 'react';
import { bucketsOf, bucketSum, verdictTone, isEmptyState } from './dashboardModel';
import './dashboard-ux.css';

// Six-level price-position taxonomy, identical to the product list's status
// chips / myprice filters. Counts come from the overview endpoint's
// total_position_* fields (see pricerest service_dashboard). Colors/labels/routes
// mirror the product list (MyPriceChips / DecisionHero).
const POS = {
  lowest:  { field: 'total_position_lowest',  color: '#16a34a', labelKey: 'product.myprice_lowest',     route: '#/product?myprice=lowest' },
  same:    { field: 'total_position_same',    color: '#2563eb', labelKey: 'product.myprice_same',       route: '#/product?myprice=same' },
  low:     { field: 'total_position_low',     color: '#0891b2', labelKey: 'product.myprice_avg_lower',  route: '#/product?myprice=avg_lower' },
  average: { field: 'total_position_average', color: '#ca8a04', labelKey: 'product.myprice_average',    route: '#/product?myprice=average' },
  high:    { field: 'total_position_high',    color: '#ea580c', labelKey: 'product.myprice_avg_higher', route: '#/product?myprice=avg_higher' },
  highest: { field: 'total_position_highest', color: '#dc2626', labelKey: 'product.myprice_highest',    route: '#/product?myprice=highest' },
};
const POS_ORDER = ['lowest', 'same', 'low', 'average', 'high', 'highest'];

const positionValue = (overview, key) => (overview[POS[key].field] || 0);
const positionTotal = (overview) => POS_ORDER.reduce((sum, key) => sum + positionValue(overview, key), 0);

// Verdict tone from the 6-level partition: cheap side (lowest/same/low) vs
// pricey side (average/high/highest). Avoids the old 4-bucket taxonomy's
// misleading red, which lumped below-average products into "higher".
const positionTone = (overview) => {
  const total = positionTotal(overview);
  if (!total) return 'gray';
  const competitive = positionValue(overview, 'lowest') + positionValue(overview, 'same') + positionValue(overview, 'low');
  const pricey = positionValue(overview, 'average') + positionValue(overview, 'high') + positionValue(overview, 'highest');
  if (competitive >= total / 2) return 'green';
  if (pricey >= total / 2) return 'red';
  return 'amber';
};

// "Pricey" = every position except lowest (the 5 myprice filter values, comma
// joined so the product list selects all five at once).
const PRICEY_ROUTE = '#/product?myprice=same,avg_lower,average,avg_higher,highest';

// Two-level summary (cheapest vs the rest) above the always-visible full 6-level
// breakdown. Both bars share the same total, so the detail is a proportional
// expansion of the summary's "pricey" segment.
const CompetitivenessBar = ({ overview, t }) => {
  const total = positionTotal(overview) || 1;
  const lowest = positionValue(overview, 'lowest');
  const others = total - lowest;
  const pct = Math.round((lowest / total) * 100);
  const R = 42;
  const C = 2 * Math.PI * R;
  const greenLen = (lowest / total) * C;
  return (
    <div className="dux-hero-cols">
      <div className="dux-summary">
        <svg className="dux-donut" width="104" height="104" viewBox="0 0 104 104" role="img" aria-label={'%' + pct + ' ' + t('product.myprice_lowest')}>
          <circle cx="52" cy="52" r={R} fill="none" stroke="#d97706" strokeWidth="14" />
          <circle cx="52" cy="52" r={R} fill="none" stroke="#16a34a" strokeWidth="14" strokeLinecap="round"
                  strokeDasharray={greenLen + ' ' + (C - greenLen)} transform="rotate(-90 52 52)" />
          <text x="52" y="49" textAnchor="middle" className="dux-donut-pct">%{pct}</text>
          <text x="52" y="67" textAnchor="middle" className="dux-donut-sub">{t('product.myprice_lowest')}</text>
        </svg>
        <div className="dux-cbar-legend dux-summary-legend">
          <button type="button" className="dux-legend-item" onClick={() => { window.open('#/product?myprice=lowest', '_blank', 'noopener'); }}>
            <span className="dux-swatch" style={{ background: '#16a34a' }} />{t('product.myprice_lowest')} {lowest}
          </button>
          <button type="button" className="dux-legend-item" onClick={() => { window.open(PRICEY_ROUTE, '_blank', 'noopener'); }}>
            <span className="dux-swatch" style={{ background: '#d97706' }} />{t('dashboard.hero.others_pricey', 'Pricey')} {others}
          </button>
        </div>
      </div>
      <div className="dux-hero-detail">
        <div className="dux-cbar" style={{ height: 12 }}>
          {POS_ORDER.filter((key) => positionValue(overview, key) > 0).map((key) => (
            <div key={key} className="dux-cbar-seg" style={{ width: (positionValue(overview, key) / total) * 100 + '%', background: POS[key].color }} />
          ))}
        </div>
        <div className="dux-cbar-legend">
          {POS_ORDER.map((key) => (
            <button key={key} type="button" className="dux-legend-item" onClick={() => { window.open(POS[key].route, '_blank', 'noopener'); }}>
              <span className="dux-swatch" style={{ background: POS[key].color }} />{t(POS[key].labelKey)} {positionValue(overview, key)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const AllowedBand = ({ overview, t }) => {
  // Price compliance (MAP or MRP): a product with any price violation is the red
  // segment (total_violated_any), matching the hero verdict + attention cards. A
  // product violating both MAP and MRP is counted once.
  const compliant = overview.total_no_violated_any || 0;
  const violation = overview.total_violated_any || 0;
  const total = compliant + violation || 1;
  return (
    <div>
      <div className="dux-cbar">
        <div className="dux-cbar-seg" style={{ width: (compliant / total) * 100 + '%', background: '#16a34a' }} />
        <div className="dux-cbar-seg" style={{ width: (violation / total) * 100 + '%', background: '#dc2626' }} />
      </div>
      <div className="dux-cbar-legend">
        <span className="dux-legend-item"><span className="dux-swatch" style={{ background: '#16a34a' }} />{t('overview.total_no_any_violation')} {compliant}</span>
        <span className="dux-legend-item"><span className="dux-swatch" style={{ background: '#dc2626' }} />{t('overview.total_any_violation')} {violation}</span>
      </div>
    </div>
  );
};

const verdictText = (profile, overview, t) => {
  const b = bucketsOf(overview);
  switch (profile.verdict) {
    case 'pricePosition':
      return t('dashboard.hero.cheapest_of', { cheapest: b.cheapest, total: bucketSum(overview) });
    case 'violationSummary': {
      const map = overview.total_violated_map || 0;
      const mrp = overview.total_violated_mrp || 0;
      const unauth = overview.total_unauthorized || 0;
      if (map + mrp + unauth === 0) return t('dashboard.hero.all_compliant', 'All resellers compliant');
      // Only mention price when there's a price violation, so an unauthorized-only
      // campaign reads "N yetkisiz…" (red) beside a green price bar — complementary,
      // not the contradictory "0 MAP · 0 MRP ihlali · N yetkisiz".
      const parts = [];
      if (map + mrp > 0) parts.push(t('dashboard.hero.violation_summary', { map, mrp }));
      if (unauth > 0) parts.push(t('dashboard.hero.unauthorized_summary', { count: unauth }));
      return parts.join(' · ');
    }
    case 'profitLoss': {
      const loss = overview.total_drop_shipping_loss || 0;
      return loss === 0 ? t('dashboard.hero.no_loss', 'No products at a loss') : t('dashboard.hero.loss_summary', { loss });
    }
    default:
      return t('dashboard.hero.plain_status', { products: overview.total_products || 0 });
  }
};

const DashboardHero = ({ profile, overview = {}, t }) => {
  if (isEmptyState(profile, overview)) {
    return (
      <div className="pux-section">
        <div className="pux-hero-verdict-label pux-hero-verdict-gray">
          {t('dashboard.empty.title', { count: overview.total_products_without_competitors || overview.total_products || 0 })}
        </div>
        <a className="pux-chip pux-chip-blue" href={profile.emptyState?.actionRoute} target="_blank" rel="noopener noreferrer" style={{ marginTop: 10, display: 'inline-block' }}>
          {t('dashboard.empty.action', 'Add competitors')}
        </a>
      </div>
    );
  }

  const tone = profile.verdict === 'pricePosition' ? positionTone(overview) : verdictTone(profile, overview);

  return (
    <div className="pux-section">
      <span className={`pux-hero-verdict-label pux-hero-verdict-${tone}`}>{verdictText(profile, overview, t)}</span>
      {profile.viz === 'competitiveness' && <CompetitivenessBar overview={overview} t={t} />}
      {profile.viz === 'allowedBand' && <AllowedBand overview={overview} t={t} />}
    </div>
  );
};

export default DashboardHero;
